import { useState } from 'react';
import {
  ActionIcon,
  Anchor,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconArchive, IconPlayerPlay, IconUpload } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi } from '@/api/admin';
import { errorMessage } from '@/api/http';
import { useVideoList } from '@/api/queries';
import type { AdminVideo, VideoPurpose, VideoStatus } from '@/api/types';
import { EmptyState } from '@/components/EmptyState';
import { ListSearch } from '@/components/ListSearch';
import { ListFooter } from '@/components/PagedList';
import { PageHeader } from '@/components/PageHeader';
import { ErrorAlert, PageLoader } from '@/components/QueryState';
import { UploadProgress } from '@/features/uploads/UploadProgress';
import { useUploads } from '@/features/uploads/context';
import { LinkTestStreamForm } from '@/features/videos/LinkTestStreamForm';
import { TEST_STREAMS_ENABLED, testStreamsUnavailable } from '@/features/videos/testStreams';
import { UploadVideoForm } from '@/features/videos/UploadVideoForm';
import { VideoPlayback } from '@/features/videos/VideoPlayback';
import { VideoSummary } from '@/features/videos/VideoSummary';
import { formatDate } from '@/lib/format';
import { useListUrlState } from '@/lib/useListUrlState';

export function VideosPage() {
  const url = useListUrlState();
  const status = url.get('status') as VideoStatus | undefined;
  const purpose = url.get('purpose') as VideoPurpose | undefined;
  const list = useVideoList({ q: url.q, status, purpose });
  const filtered = !!(status || purpose || url.q);
  const [addOpen, addModal] = useDisclosure();
  const [playing, setPlaying] = useState<AdminVideo | null>(null);
  const { uploads } = useUploads();
  const qc = useQueryClient();
  const archive = useMutation({
    mutationFn: (id: string) => adminApi.archiveVideo(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['videos'] });
      notifications.show({ color: 'leaf', message: 'Video archived.' });
    },
    onError: (e) =>
      notifications.show({ color: 'coral', title: 'Not archived', message: errorMessage(e) }),
  });

  return (
    <>
      <PageHeader
        title="Videos"
        description="Episode videos and preview clips, encoded by Bunny Stream. Uploads continue while you work elsewhere in the admin."
        actions={
          <Button leftSection={<IconUpload size={16} />} onClick={addModal.open}>
            Upload video
          </Button>
        }
      />
      {uploads.length > 0 && (
        <Stack
          gap="sm"
          mb="md"
          p="sm"
          style={{ border: '1px solid var(--pb-hairline)', borderRadius: 8 }}
          aria-label="Uploads in progress"
        >
          {uploads.map((u) => (
            <UploadProgress key={u.key} upload={u} />
          ))}
        </Stack>
      )}
      <Group gap="sm" mb="md">
        <ListSearch
          label="Search videos"
          placeholder="Search videos"
          value={url.q}
          onSearch={url.setQ}
        />
        <Select
          aria-label="Status"
          placeholder="Any status"
          clearable
          value={status ?? null}
          onChange={(v) => url.setFilter('status', v)}
          data={[
            { value: 'uploading', label: 'Uploading' },
            { value: 'processing', label: 'Processing' },
            { value: 'ready', label: 'Ready' },
            { value: 'errored', label: 'Failed' },
          ]}
          w={160}
        />
        <Select
          aria-label="Purpose"
          placeholder="Episodes and previews"
          clearable
          value={purpose ?? null}
          onChange={(v) => url.setFilter('purpose', v)}
          data={[
            { value: 'episode', label: 'Episodes' },
            { value: 'preview', label: 'Preview clips' },
          ]}
          w={200}
        />
      </Group>
      {list.isPending ? (
        <PageLoader label="Loading videos…" />
      ) : list.error ? (
        <ErrorAlert error={list.error} onRetry={() => void list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <EmptyState
          title={filtered ? 'No videos match' : 'No videos'}
          action={
            <Button variant="default" onClick={addModal.open}>
              Upload video
            </Button>
          }
        >
          {filtered
            ? 'Try clearing a filter.'
            : 'Upload a file here, or from an episode in the programme editor.'}
        </EmptyState>
      ) : (
        <>
          <Table.ScrollContainer minWidth={760}>
            <Table striped style={{ opacity: list.isPlaceholderData ? 0.6 : 1 }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Video</Table.Th>
                  <Table.Th>Purpose</Table.Th>
                  <Table.Th>Size</Table.Th>
                  <Table.Th>Used by</Table.Th>
                  <Table.Th>Added</Table.Th>
                  <Table.Th w={80} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.data.items.map((v) => (
                  <Table.Tr key={v.id}>
                    <Table.Td maw={360}>
                      <VideoSummary video={v} />
                      <Text size="xs" c="dimmed">
                        {v.provider === 'static' ? 'Test stream' : 'Bunny Stream'}
                        {v.has_captions ? ' · captions' : ''}
                      </Text>
                    </Table.Td>
                    <Table.Td>{v.purpose === 'preview' ? 'Preview clip' : 'Episode'}</Table.Td>
                    <Table.Td>{v.width && v.height ? `${v.width}×${v.height}` : '—'}</Table.Td>
                    <Table.Td>
                      {v.used_by.length === 0 ? (
                        <Text size="sm" c="dimmed">
                          Not used
                        </Text>
                      ) : (
                        <Stack gap={0}>
                          {v.used_by.map((u) => (
                            <Anchor
                              key={`${u.kind}-${u.id}`}
                              component={Link}
                              to={`/programmes/${u.programme_id}/${u.kind === 'preview' ? 'preview' : 'episodes'}`}
                              size="sm"
                            >
                              {u.kind === 'preview' ? `Preview: ${u.title}` : u.title}
                            </Anchor>
                          ))}
                        </Stack>
                      )}
                    </Table.Td>
                    <Table.Td>{formatDate(v.created_at)}</Table.Td>
                    <Table.Td>
                      <Group gap={2} wrap="nowrap">
                        <Tooltip label="Play">
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            aria-label={`Play ${v.title}`}
                            disabled={v.status !== 'ready'}
                            onClick={() => setPlaying(v)}
                          >
                            <IconPlayerPlay size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={v.used_by.length ? 'In use — detach it first' : 'Archive'}>
                          <ActionIcon
                            variant="subtle"
                            color="coral"
                            aria-label={`Archive ${v.title}`}
                            disabled={v.used_by.length > 0}
                            onClick={() =>
                              modals.openConfirmModal({
                                title: `Archive “${v.title}”?`,
                                children: (
                                  <Text size="sm">
                                    It disappears from the library. Nothing uses it.
                                  </Text>
                                ),
                                labels: { confirm: 'Archive', cancel: 'Cancel' },
                                confirmProps: { color: 'coral' },
                                onConfirm: () => archive.mutate(v.id),
                              })
                            }
                          >
                            <IconArchive size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <ListFooter query={list} noun="videos" />
        </>
      )}

      <Modal opened={addOpen} onClose={addModal.close} title="Add a video" size="lg">
        <AddVideo onDone={addModal.close} />
      </Modal>
      <Modal opened={!!playing} onClose={() => setPlaying(null)} title={playing?.title} size="xl">
        {playing && <VideoPlayback video={playing} />}
      </Modal>
    </>
  );
}

function AddVideo({ onDone }: { onDone: () => void }) {
  const [purpose, setPurpose] = useState<VideoPurpose>('episode');
  return (
    <Stack>
      <Select
        label="Purpose"
        value={purpose}
        onChange={(v) => setPurpose((v as VideoPurpose) ?? 'episode')}
        allowDeselect={false}
        data={[
          { value: 'episode', label: 'Episode (full programme video)' },
          { value: 'preview', label: 'Preview clip (short)' },
        ]}
      />
      <Tabs defaultValue="upload">
        <Tabs.List mb="md">
          <Tabs.Tab value="upload">Upload file</Tabs.Tab>
          {TEST_STREAMS_ENABLED && !testStreamsUnavailable() && (
            <Tabs.Tab value="static">Link test stream</Tabs.Tab>
          )}
        </Tabs.List>
        <Tabs.Panel value="upload">
          <UploadVideoForm purpose={purpose} onCreated={onDone} />
        </Tabs.Panel>
        {TEST_STREAMS_ENABLED && (
          <Tabs.Panel value="static">
            <LinkTestStreamForm purpose={purpose} onLinked={onDone} />
          </Tabs.Panel>
        )}
      </Tabs>
    </Stack>
  );
}
